// R6 — the shell's own manifest. NOT the old console's: this one models a TWO-LEVEL
// information architecture (section → app → screen), which the old AppManifest cannot
// express, and it carries `grantResourceType` so that grant-derived visibility (spine D-10)
// can be scoped rather than global.
//
// ⭐ THE EXTENSION POINT. Adding an R7 screen is adding ONE object to APP_SCOPED_ENTRIES
// below. Nothing else — no route, no rail edit, no header edit. If a future change needs
// more than that, the frame has degraded into an app and the change is wrong
// (PROPOSAL.md §3.1: "the mistake to avoid is building an app-admin SPA that later has to
// become a shell").
import type { ComponentType } from 'react';
import { DATA_PLANE_CREATE_SEGMENT, DP_TABS, DP_TAB_ICONS, DP_TAB_LABELS, dpTabSegment } from '../panes/dp-tabs';
/*
 * ⭐ The Grants entry's gate, IMPORTED rather than re-typed, so the manifest and the api module
 * cannot drift (R9 §2.3). The value it names is the one correction SR-1 found on this screen: the
 * old console gated a READ screen on `cp:permission:grant`, a WRITE permission.
 *
 * ⚠️ A VALUE import from `shell/` into `api/` — the established direction (`apps-context.tsx`
 * imports `listApps` the same way), and no cycle: `identity.api.ts` reaches only
 * `platform/http/client`, which imports `runtime-config` and `paths` and nothing from `shell/`.
 */
import { GRANT_READ_PERMISSION, OPERATOR_READ_PERMISSION, PERMISSION_GRANT_PERMISSION, ROLE_READ_PERMISSION } from '../api/identity.api';
/* The Members gate names the three list reads through the constants their api modules own. */
import { GROUP_READ_PERMISSION } from '../api/groups.api';
/*
 * ⭐ The `platform-billing` entry's gate, IMPORTED for the same reason: ONE definition of the
 * string, in the api module that owns the routes it guards. Re-scope or re-spell the permission there
 * and `tsc` walks every reference; a literal here keeps the old spelling silently, and the entry then
 * gates on a permission the producer no longer issues — a door nobody can open, or worse, one nobody
 * is stopped at.
 *
 * ⛔ **CORRECTED. This comment used to claim a literal is "INVISIBLE" to `dev-personas.test.ts`'s
 * persona scan. That is false, and a false justification is worse than none — it invites the next
 * reader to disprove it and then discard the rule with it.** Measured: `dev-personas.test.ts` imports
 * this module and reads `entry.requiredPermission` as a RUNTIME VALUE (`:24`, `:270-272`), so a
 * literal and a constant are equally visible to it. Its text-scanning half (`permissionConstants` /
 * `surfaceObligations`) has corpus `src/api/**` and `src/panes/**` and never opens this file at all —
 * so no manifest spelling is visible to that half either. ⇒ **the reason is single-source-of-truth
 * and rename safety, and that reason is sufficient on its own.**
 */
import { BILLING_READ_PERMISSION } from '../api/billing.api';
/*
 * ⭐ Same rule, same reason — the gates for `platform-cp-logs`, the two `platform-*` settings screens
 * and `app-adoption`. These four sites carried literals; the constants already existed in the api
 * modules that own the routes, which is exactly the drift this import style prevents.
 */
import { ADOPTION_PERMISSION } from '../api/appRollout.api';
import { APP_BUILD_PERMISSION } from '../api/appBuild.api';
import { CP_POD_LOG_READ_PERMISSION } from '../api/cpPods.api';
import { SETTINGS_READ_PERMISSION } from '../api/settings.api';
import { PRIVATE_CHANNEL_READ_PERMISSION } from '../api/private-channels.api';
import { KERNEL_RIGHTS_READ_PERMISSION } from '../api/kernelRights.api';
import { KERNEL_RIGHTS_DECIDE_PERMISSION } from '../api/kernelRightsQueue.api';
import { LIBRARY_RIGHTS_READ_PERMISSION } from '../api/libraryRights.api';
import { LIBRARY_RIGHTS_DECIDE_PERMISSION } from '../api/libraryRightsQueue.api';
/* The onboarding entry's three grants, imported from the api module that owns the routes (WS-16). */
import { ONBOARDING_CREATE_PERMISSION, ONBOARDING_READ_PERMISSION, ONBOARDING_TENANT_PERMISSION } from '../api/onboarding.api';
/* The Identity token entry's gate, imported from the api module that owns the route (D11). */
import { IDP_READ_PERMISSION } from '../api/authProviders.api';
/* The Machine identities entry's gate and route, from the modules that own them (WS-17). */
import { MACHINE_PRINCIPAL_READ_PERMISSION } from '../api/machineIdentities.api';
import { MACHINE_IDENTITIES_ROUTE, MACHINE_IDENTITY_PARAM } from '../panes/machine-identity-model';

/**
 * Permission requirement: a flat string or an "any of" union.
 *
 * ⛔ DECLARED HERE rather than shared from anywhere else. It is four tokens of type; routing
 * something this small through a shared module would be more code than declaring it, and it
 * keeps the manifest's permission vocabulary readable in one file.
 */
export type ManifestPermission = string | { readonly anyOf: readonly string[] };

/**
 * Top-level sections: the R7 information architecture (R7-DEC-2), regrouped into six by plan
 * 2026-10-14-console-nav-rail (D5).
 *
 * ⛔ `identity` and `observability` were MEMBERS of this union in R6 and are **DELETED**, not
 * deprecated. "Add them back, they were only removed to keep the union small" is the
 * plausible-looking wrong edit, so the rationale lives here rather than only in the spine:
 *   · **`Access` REPLACES Identity.** The deck collapses eight Identity rail entries into five
 *     job-shaped screens (mockups `README.md:68`). Keeping `identity` in the union re-advertises
 *     a section whose screens now live under `access`.
 *   · **A top-level Observability section is REFUSED**, not deferred. Every observability route in
 *     the CP is scoped to a DP or an app — there is no global log, metric or trace search
 *     (`README.md:88-95`; the route census confirms every `logs/query`, `metrics/query` and
 *     `traces` route sits under `/v1/admin/data-planes/:id/…` or `/v1/admin/apps/:appId/…`). A
 *     section for it would be a door onto a producer that does not exist.
 *   · `investigate` is a different section and adds no producer (plan I2). It groups five global
 *     screens that exist already: Alerts, Control-plane logs, Dead letters, Grafana and Audit. It
 *     holds no global log, metric or trace search.
 *
 * ⭐ **`settings` JOINED THE UNION IN R9 (N-1), AND IT IS NOT A RENAME OF THE DELETED `identity`.**
 * The old console's one "Identity" section was two different jobs, which is what made it incoherent:
 *   · Access is authorization: Members (People, Groups and Roles), App access and Check access on
 *     the rail, and the Cutover dry-run off the rail, reached from the Roles banner.
 *   · Settings is platform configuration and identity integrations: Platform settings,
 *     Observability retention, Identity providers, SCIM clients, OAuth clients, Registration hosts.
 * An IdP connector configures *where identities come from*; a grant confers *authority*. Different
 * audiences, different blast radius. ⛔ So "add `identity` back, `settings` is nearly the same thing"
 * is the plausible-looking wrong edit — it re-merges the two jobs N-1 separated.
 *
 * ⚠️ Adding a member here still ships NOTHING on its own. A section RENDERS iff at least one entry
 * the principal can see declares it (`buildLandingCandidates`, `landing.ts`).
 */
export type ShellSection = 'platform' | 'investigate' | 'apps' | 'releases' | 'access' | 'settings';

/**
 * The information architecture's order, and NOTHING ELSE. Platform first, because that is where
 * an operator lands (the June tenant-first ruling, `mockups/shell.js:66-69`).
 *
 * ⛔ This is a TOTAL PERMUTATION of `ShellSection`, not a shipped-set switch. A section RENDERS
 * iff at least one entry the principal can see declares it (`buildLandingCandidates`), so listing
 * a section here ships nothing on its own, and a section with no entries can never render a dead
 * tab — the property R6 enforced with a test is now true by construction.
 *
 * ⚠️ This is why WS-0 lands green before any Platform screen exists: the sections are named here,
 * `GLOBAL_ENTRIES` still declares only `apps`, and exactly one section renders.
 *
 * ## ⭐⭐ R9: THIS ARRAY IS NOW THE RAIL'S ORDERING AUTHORITY, NOT JUST THE STRIP'S
 *
 * The rail groups by SECTION and iterates this array (`NavRail`'s `groupRailBySection`). ⛔ It
 * REPLACED `groupRailEntries`, which ordered groups by whichever entry happened to be declared
 * first — so a manifest reorder silently reshuffled the rail. That was a latent defect, not a
 * feature (lead ruling on Q1). Ordering now lives in exactly one place, and it is this line.
 *
 * ⛔ **`settings` IS LAST, DELIBERATELY.** Hick's law: it is the lowest-frequency,
 * highest-blast-radius group and does not belong in the primary scan path. Platform stays FIRST (the
 * June tenant-first ruling). `investigate` is second (plan I2): an operator opens it during an
 * incident, right after the fleet.
 */
export const SECTION_ORDER: readonly ShellSection[] = [
  'platform',
  'investigate',
  'apps',
  'releases',
  'access',
  'settings',
];

/**
 * The rail's group headings, and the strip's before it.
 *
 * ⛔ **THE REFERENCE LABELS ITS FIRST GROUP `Control plane`, AND THAT LABEL IS SUPERSEDED**
 * (`02-NAV-AND-PORT.md` §2.4). With six sections the whole rail *is* the control plane's
 * navigation, so a group called "Control plane" beside five others that are also the control plane is
 * a category error. Group labels are these, in sentence case (plan D4).
 */
export const SECTION_LABELS: Readonly<Record<ShellSection, string>> = {
  platform: 'Platform',
  investigate: 'Investigate',
  apps: 'Apps',
  releases: 'Releases',
  access: 'Access',
  settings: 'Settings',
};

/** Which resource kind a grant must be ON for it to make an entry visible. */
export type GrantResourceType = 'app'; // mirrors cp_auth.resource_grants.resource_type

export interface ShellEntry {
  /** Stable id — routing key, lazy-cache key, and `data-testid` suffix. */
  readonly id: string;
  readonly section: ShellSection;
  readonly displayName: string;
  /** lucide-react component name, resolved by SectionRail's ICONS map. */
  readonly iconKey: string;
  readonly loader: () => Promise<{ default: ComponentType }>;
  readonly requiredPermission: ManifestPermission;
  /**
   * Resource kind whose grants can confer visibility (D-10). `undefined` ⇒ site-permission
   * visibility only.
   */
  readonly grantResourceType?: GrantResourceType;
  /*
   * ⛔⛔ **`railGroup?: string` IS RETIRED (R9 Q1, APPROVED). THE SECTION IS THE GROUP.**
   *
   * It existed because, under the old IA, a section was a top-bar word and the rail showed exactly
   * ONE section — so an in-rail divider was the only grouping available, and `Operations` (In flight +
   * Alerts) was its one production use. Under the new IA the rail shows all six sections as labelled
   * groups, so a SECOND label weight *inside* a group is a hierarchy the rail cannot express without a
   * third type treatment, and Platform at six declared entries, five on the rail, does not need one.
   *
   * ⇒ ⭐ **RULE FOR THE FUTURE: if a section exceeds ~8 entries, SPLIT THE SECTION. Never sub-group
   * it.** Re-adding a field here is how the rail grows a second hierarchy nobody designed a type
   * treatment for.
   */
}

/**
 * A detail screen reached by clicking a row (R7-DEC-4). It has NO rail entry of its own: the
 * PARENT entry stays lit and breadcrumbs say where you are (`mockups/shell.js:206-217`).
 *
 * ⛔ Considered and rejected: a `railHidden: true` flag on an ordinary entry. That puts a
 * rail-visibility question inside the object the rail filter reads, and invites `visibleGlobal` to
 * contain something the rail must not render. `detail` is unreachable from the rail by
 * construction.
 *
 * A whole screen with its own route that is parked for a while is a different case, and
 * {@link GlobalEntry.navHidden} serves it. A detail screen still uses this slot, so its parent
 * stays lit.
 */
export interface EntryDetail {
  /**
   * Unique id. ⛔ It must NOT equal the parent's: `AppLoader` caches `lazy(loader)` at module
   * scope keyed by `entry.id` (`AppLoader.tsx:33-41`), so a shared id would serve the LIST pane
   * on the detail route — a wrong screen with no error.
   */
  readonly id: string;
  /** Route param name. The route is `${routePrefix}/:${paramName}`. */
  readonly paramName: string;
  readonly loader: () => Promise<{ default: ComponentType }>;
  /**
   * ⭐⭐ **R9 N-11 / N-12 — the record's own destinations, as a SCOPED RAIL GROUP and REAL ROUTES.**
   * `undefined` ⇒ this detail screen is one page, which is what every other detail screen is today.
   *
   * @see DetailScope for the shape and for why the option is per-entry rather than blanket.
   */
  readonly scoped?: DetailScope;
}

/**
 * One destination inside a detail scope. ⛔ Deliberately the same shape as {@link AppScopedEntry}
 * minus the parts that cannot apply, and the omissions are the interesting half.
 *
 * ## ⛔ NO `loader` — AND THIS IS THE CONSTRAINT THAT CHOSE THE WHOLE ROUTING SHAPE
 *
 * An app-scoped entry gets its own `AppLoader`, so switching destinations UNMOUNTS the previous
 * screen. A detail scope cannot work that way: `DataPlanePane` owns **six `useApiResource` calls**
 * for the record, and its own header states why — *"a tab body unmounts when you leave it, so a read
 * inside one re-fires on every tab switch — the operator would watch data they already had flick
 * back to 'Loading…' each time."* Six sibling `<Route>`s with six loaders would reinstate exactly
 * that defect while looking like the tidier design.
 *
 * ⇒ **ONE splat route, one mounted pane, six destinations** (R9-14). The pane reads the trailing
 * segment and renders the body; React reconciles because the route element never changes.
 *
 * ## ⛔ NO `requiredPermission`, AND IT IS A RULING RATHER THAN AN OVERSIGHT
 *
 * The obvious symmetry with `AppScopedEntry` is a per-destination gate. It is refused here on a
 * ground this package already recorded, at `DataPlaneOverviewTab.tsx`'s metrics door: *"No permission
 * gate on the DOOR, deliberately — the gate lives on the screen it opens… Hiding the door instead
 * would make the screen unreachable-and-unexplained, which is the strictly worse failure: a missing
 * affordance is indistinguishable from a bug."*
 *
 * Every one of the DP's six destinations degrades **inside itself**, by panel, naming the grant it
 * wants (`DataPlaneDiagnosticsTab`'s three separately-holdable observability permissions;
 * `DataPlaneMetricsScreen`'s `ForbiddenState`). A rail gate would hide those explanations. ⇒ A detail
 * scope's destinations inherit the PARENT entry's gate — if you can open the record, you can see
 * where you may go in it. ⚠️ A future scope that genuinely needs per-destination gating adds the
 * field HERE and a filter in `AppHost`; it needs no `NavRail` change, which is the property R9-13
 * asked for.
 */
export interface DetailScopedEntry {
  /** Stable id — the rail's `data-testid` suffix (`nav-${id}`). ⛔ Unique across the whole rail. */
  readonly id: string;
  readonly displayName: string;
  /** lucide-react component name, resolved by `NavRail`'s `ICONS` map. */
  readonly iconKey: string;
  /**
   * Relative to `${routePrefix}/:${paramName}`. ⛔ The empty string is the record's OWN URL, exactly
   * as `app-overview` carries `subPath: ''` — so the record root and its first destination are one
   * URL, not two.
   */
  readonly subPath: string;
}

/**
 * A detail screen's destinations, plus the word the rail calls them by.
 *
 * ## ⭐ WHY THIS IS OPT-IN PER ENTRY AND NOT A BLANKET SPLAT (R9-14)
 *
 * The cheap implementation is to give EVERY entry with a `detail` a `${routePrefix}/:${param}/*`
 * route. ⛔ That silently changes two screens that are not part of this work:
 * `/access/people/:userId/garbage` and `/platform/tenants/:tenantId/garbage` would render the detail
 * pane instead of redirecting, i.e. two new URLs that resolve to a screen nobody asked for. An
 * opt-in field means a scope exists exactly where someone declared one.
 */
export interface DetailScope {
  /**
   * The rail group's leading word — `Data plane`, later `Tenant`. Rendered as `<label> · <subject>`.
   *
   * ⛔ **THIS IS WHY THE RAIL STOPPED SAYING `App`.** `NavRail` hard-coded the word, so the scoped
   * group was app-shaped rather than scope-shaped and a second adopter could not exist without
   * editing it. R9-13: the tenant adoption after this one must be a **pure adoption with zero
   * library change**.
   */
  readonly label: string;
  /** ⛔ Declaration order IS rail order. The first entry is the record's landing destination. */
  readonly entries: readonly DetailScopedEntry[];
}

/**
 * A CREATE screen reached from the parent's `PageHeading.primaryAction` (#1242). Like
 * {@link EntryDetail} it has **no rail entry of its own** — the parent stays lit.
 *
 * ## ⛔ WHY A NESTED SLOT AND NOT AN ORDINARY ENTRY
 *
 * `AppHost` builds `railItems` from every visible global entry that is not `navHidden`, so a
 * create screen declared as an ordinary entry ships as a **permanent Platform nav item** — a
 * standing link to a blank form beside Tenants and Data planes.
 *
 * ⛔ And the obvious escape hatch is already rejected directly above, for {@link EntryDetail}: a
 * `railHidden: true` flag *"puts a rail-visibility question inside the object the rail filter reads,
 * and invites `visibleGlobal` to contain something the rail must not render."* A nested slot is
 * **unreachable from the rail by construction**, which is the property that matters.
 * Two rail flags exist, and both serve a different case: a whole screen with its own route, kept
 * off the rail. {@link GlobalEntry.navHidden} parks a global screen, and `onRail` filters it.
 * {@link AppScopedEntry.navHidden} takes an app screen off the app rail group, and `AppHost` filters
 * the scoped rail items on it.
 *
 * ## ⭐ TWO INDEPENDENT MECHANISMS AGREE, AND THAT IS WHY THIS SHAPE IS RIGHT RATHER THAN CONVENIENT
 *
 * 1. **Route ranking** — react-router ranks the static `…/new` above the sibling dynamic
 *    `…/:paramName`, so a create URL never falls into the detail route as `paramName="new"`.
 * 2. **Section resolution** — {@link resolveActiveSection} is longest-prefix over `routePrefix`, so
 *    `/platform/tenants/new` resolves through its parent and Platform stays lit with no extra rule.
 *
 * ⛔ **Both are asserted in `entry-create.test.tsx`, and (1) especially**: it is a claim about a
 * LIBRARY's ranking, not about our code, and the day it changes the symptom is a detail page
 * rendering for an entity that does not exist. ⚠️ *A behaviour you depend on but do not own needs a
 * test more than one you wrote yourself, not less.*
 */
export interface EntryCreate {
  /**
   * Unique id. ⛔ It must NOT equal the parent's — same reason as {@link EntryDetail.id}:
   * `AppLoader` caches `lazy(loader)` at module scope keyed by `entry.id`, so a shared id serves
   * the LIST pane on the create route. A wrong screen, with no error.
   */
  readonly id: string;
  /** The static segment. The route is `${routePrefix}/${segment}`. */
  readonly segment: string;
  readonly loader: () => Promise<{ default: ComponentType }>;
}

/** Entries that are NOT scoped to one app — `routePrefix` is absolute. */
export interface GlobalEntry extends ShellEntry {
  readonly routePrefix: string;
  readonly detail?: EntryDetail;
  readonly create?: EntryCreate;
  /**
   * Takes a screen off the rail, and keeps its route. Two kinds of screen use it: a parked screen,
   * and a screen whose door is a link on another screen and whose parent has no free nested slot
   * (`platform-onboarding`, with its door on `TenantsPane`). A section that holds only hidden
   * entries lands after every section with a rail entry. The entry stays in `visibleGlobal`, so its
   * permission still gates the route. No rail item lights on the hidden route, because no rail item
   * matches it. A detail or create screen uses the nested slots when its parent has one free, so its
   * parent stays lit. Delete the flag of a parked screen to bring the screen back; a screen with a
   * door elsewhere keeps the flag.
   */
  readonly navHidden?: true;
  /**
   * The id of the rail entry this one sits under, indented (access-members design, revised
   * 2026-10-15). People, Groups and Roles name `access-members`. The rail shows the children only
   * while the current page is the parent's or a child's, so a press on the parent, which lands on the
   * first child, opens them. The parent must be a rail entry of the same section with no parent of its
   * own. Absent: the entry sits at the top level of its section.
   */
  readonly navParent?: string;
}

/** Whether the rail shows this entry: every entry but a `navHidden` one. */
export function onRail(entry: GlobalEntry): boolean {
  return entry.navHidden !== true;
}

/**
 * Entries INSIDE an app. `subPath` is relative to `/apps/:appId`; the empty string is the
 * app's own landing screen.
 */
export interface AppScopedEntry extends Omit<ShellEntry, 'section'> {
  /**
   * ⛔ LITERALLY `'apps'`. App scoping is what `/apps/:appId/*` means; a "platform-scoped"
   * app-scoped entry is not a thing, and allowing the wider type would make
   * `buildLandingCandidates` need an arm for "a non-Apps section visible only through an
   * app-scoped entry" — an arm that cannot be reached and therefore cannot be covered.
   */
  readonly section: 'apps';
  readonly subPath: string;
  /**
   * Takes the screen off the app rail group and keeps its route, as {@link GlobalEntry.navHidden}
   * does for a global entry. The entry stays in `visibleAppScoped`, so its permission still gates the
   * route and `AppScopeRoute` still serves `/apps/:appId/<subPath>`. `AppHost` builds the scoped rail
   * items from the visible entries that are not `navHidden`. Delete the flag to bring the rail item
   * back. `app-build` is the one user (plan 2026-10-13-console-ward-app-drill-in, D2 and A-4).
   */
  readonly navHidden?: true;
}

/**
 * The `ShellEntry` an `AppLoader` needs for a detail route. Takes the DETAIL, not the parent, so
 * there is no narrowing and no non-null assertion at the call site.
 *
 * ⚠️ It returns a fresh object each call. That is safe **because** `AppLoader` reads only `.id` and
 * `.loader` and caches on the id — do not "optimise" it into a memo that keys on the parent
 * reference, which churns on every permission-snapshot recompute (`AppLoader.tsx:14-16`).
 */
export function detailShellEntry(parent: GlobalEntry, detail: EntryDetail): ShellEntry {
  return { ...parent, id: detail.id, loader: detail.loader };
}

/**
 * The `ShellEntry` an `AppLoader` needs for a CREATE route. Symmetric with
 * {@link detailShellEntry}, and deliberately a separate function rather than one taking a union:
 * the two slots are different concepts, and a shared helper would be the first place a future
 * `railHidden`-style flag gets smuggled in.
 */
export function createShellEntry(parent: GlobalEntry, create: EntryCreate): ShellEntry {
  return { ...parent, id: create.id, loader: create.loader };
}

/**
 * ⭐⭐ **THE DATA PLANE'S SIX DESTINATIONS — A PROJECTION OF `dp-tabs.ts`, NOT A SECOND REGISTRY.**
 *
 * ⛔ **THE `.map` IS THE POINT AND IT MUST NOT BE "TIDIED" INTO SIX LITERALS.** N-12's fourth and
 * deciding reason for making these routes was that `DP_TABS` was *"a second registry the manifest
 * does not know about"*, so the rail would be generated from the manifest for global entries and
 * from a per-pane array for scoped ones. Writing the six out here would re-create precisely that —
 * two lists that agree today and drift the first time someone adds a seventh destination to only
 * one of them. **A seventh destination is one edit, in `dp-tabs.ts`, and it appears here, in the
 * rail, and as a route, with no further change.**
 *
 * ⚠️ The static import direction (`shell/manifest.ts → panes/dp-tabs.ts`) is the direction this file
 * already runs in — every entry's `loader` names a pane — and `dp-tabs.ts` imports nothing from
 * `shell/`, so there is no cycle. That constraint is recorded in its header; do not add one.
 *
 * ⚠️ `id` is prefixed `dp-` and NOT reused from anywhere: it becomes `data-testid="nav-dp-logs"`, and
 * `nav-app-logs` already exists for the app-scoped Pod logs screen. Two rail links with one test id
 * is a `getByTestId` that throws only when both scopes can render, which is never — so it would be
 * an id collision no test could catch.
 */
export const DP_SCOPED_ENTRIES: readonly DetailScopedEntry[] = DP_TABS.map((tab) => ({
  id: `dp-${tab}`,
  displayName: DP_TAB_LABELS[tab],
  iconKey: DP_TAB_ICONS[tab],
  subPath: dpTabSegment(tab),
}));

export const GLOBAL_ENTRIES: readonly GlobalEntry[] = [
  {
    // ⛔ FIRST among `platform` entries, and that is load-bearing rather than tidy:
    // declaration order IS the landing mechanism (`buildLandingCandidates` takes the first
    // visible entry in a section). Moving this below a future Platform entry silently
    // changes where a platform operator lands after sign-in.
    id: 'platform-tenants',
    section: 'platform',
    displayName: 'Tenants',
    routePrefix: '/platform/tenants',
    iconKey: 'Building2',
    loader: () => import('../panes/TenantsPane'),
    requiredPermission: 'cp:tenant:read',
    // ⛔ NO `grantResourceType`. `GrantResourceType` is only `'app'`, and A22 requires every
    // non-Apps entry to omit it — a resource grant on an app must never light up Platform.
    detail: {
      id: 'platform-tenant-detail',
      paramName: 'tenantId',
      loader: () => import('../panes/TenantPane'),
    },
    /*
     * ⛔ R8-2. The owner's first listed defect was "No way to add a new tenant" — and the
     * diagnosis was that nothing was DELETED: the create surface was NEVER MIGRATED. A working
     * `NewTenantPage` has existed in the old console throughout, and `POST /v1/orgs` has always
     * been live.
     *
     * ⚠️ `create`, not an ordinary entry, and the difference is load-bearing: `AppHost` builds
     * `railItems` from every visible `GlobalEntry` that is not `navHidden`, so a sibling entry would
     * ship "New tenant" as a permanent Platform nav item. See this file's own note above: the
     * shell rejects a `railHidden` flag for create and detail screens, and `navHidden` serves a
     * whole parked screen, or a screen with a door on another page when this one slot is taken
     * (`platform-onboarding` below).
     */
    create: {
      id: 'platform-tenant-create',
      segment: 'new',
      loader: () => import('../panes/NewTenantPane'),
    },
  },
  {
    /*
     * Tenant onboarding (plan 2026-10-16-customer-onboarding, WS-16, R-072 item 1): the wizard at
     * `/platform/onboard` and the progress view of one request at `?request=<id>`. It sits after
     * `platform-tenants` (the shared-file register of the plan's index), so Platform keeps landing on
     * Tenants.
     *
     * An entry of its own, not a `create` slot: the Tenants entry already uses its one slot for
     * `new`, and `EntryCreate` takes one static segment. `navHidden`, because the door is the
     * "Onboard a tenant" link on `TenantsPane` beside "New tenant", and a rail item would be a
     * standing link to a form. No rail item lights on `/platform/onboard`, so Tenants is not lit there.
     *
     * The gate admits any one of three grants: the two create grants of the wizard, and `cp:dp:read`
     * of the progress view, which the MCP tool `onboard_tenant` links to. One create grant alone opens
     * no part of the page: the wizard opens only when both create grants hold. The pane names the
     * missing grant in its own `ForbiddenState`. No `grantResourceType` (A22): an app grant never
     * lights up Platform.
     */
    id: 'platform-onboarding',
    section: 'platform',
    displayName: 'Onboard a tenant',
    routePrefix: '/platform/onboard',
    /* The Tenants glyph, already in `NavRail`'s map. The entry is off the rail, so no rail row draws it twice. */
    iconKey: 'Building2',
    loader: () => import('../panes/OnboardTenantPane'),
    requiredPermission: { anyOf: [ONBOARDING_CREATE_PERMISSION, ONBOARDING_TENANT_PERMISSION, ONBOARDING_READ_PERMISSION] },
    navHidden: true,
  },
  {
    // ⛔ AFTER `platform-tenants`, deliberately: Platform must keep LANDING on Tenants
    // (`buildLandingCandidates` takes the first visible entry in a section). Moving this above
    // it silently changes where an operator arrives after sign-in.
    id: 'platform-data-planes',
    section: 'platform',
    displayName: 'Data planes',
    routePrefix: '/platform/data-planes',
    iconKey: 'Server',
    loader: () => import('../panes/DataPlanesPane'),
    // Site-wide (`dp-admin.controller.ts:24-25`), NOT resource-scoped.
    requiredPermission: 'cp:dp:read',
    // ⛔ NO `grantResourceType` — `GrantResourceType` is only 'app', and A22 requires every
    // non-Apps entry to omit it. An app grant must never light up Platform.
    detail: {
      id: 'platform-data-plane-detail',
      paramName: 'dpId',
      loader: () => import('../panes/DataPlanePane'),
      /*
       * ⭐⭐ **THE SECOND ADOPTER OF THE SCOPED-RAIL PRECEDENT** (N-11), and the owner set it
       * explicitly: *"by moving apps into the LHM like that we are now setting a precedent that we
       * need to follow for any top level choice like that, specifically tenants and data planes."*
       *
       * ⚠️ **Tenants is NOT the third adopter yet, and its absence here is a decision.** `TenantPane`
       * is 1,538 lines with no tab strip at all, so "follow the precedent" there means *deciding
       * which sections of a long page become destinations* — a product question with its own scope,
       * held for an explicit owner instruction rather than folded in silently. The difference
       * matters: this entry is a like-for-like conversion of an existing control, that one would be
       * a new information architecture.
       */
      scoped: {
        label: 'Data plane',
        entries: DP_SCOPED_ENTRIES,
      },
    },
    // ⛔ WITHOUT THIS BLOCK THE "New data plane" BUTTON FALLS THROUGH TO `*` AND REDIRECTS TO THE
    // LANDING SCREEN — and `operator-action-parity.test.ts` stays GREEN while it does, because it
    // asserts `consumers(action.apiFn).length > 0`: that a symbol is imported somewhere, not that
    // a URL reaches it. Measured 2026-08-07 with the pane present and this entry absent: 15/15
    // green, operator cannot create a data plane. ⇒ the create chain is endpoint → consumer →
    // ROUTE → door, and that gate reads link two of four.
    create: {
      id: 'platform-data-plane-create',
      /* One value with the id that `dataPlaneRoute` refuses, so a server id never opens this screen. */
      segment: DATA_PLANE_CREATE_SEGMENT,
      loader: () => import('../panes/NewDataPlanePane'),
    },
  },
  {
    // APPEND, third in the ungrouped Platform cluster — `mockups/shell.js` declares the primary
    // cluster as exactly Tenants → Data planes → Capacity, so this position is the deck's, not a
    // preference. It stays BELOW `platform-tenants` for the landing reason above.
    //
    // ⚠️ RE-ROUTED. This entry was reverted once (c929e5c1f) because `FleetCapacityPane.tsx` was
    // still UNTRACKED — present on disk, absent from the repo, so CI could not resolve it while
    // every local gate passed. The pane is committed now, and `manifest-icons.test.ts` carries a
    // tracked-ness row so the same mistake reds locally instead of in CI.
    id: 'platform-capacity',
    section: 'platform',
    displayName: 'Capacity',
    routePrefix: '/platform/capacity',
    iconKey: 'Gauge',
    loader: () => import('../panes/FleetCapacityPane'),
    /*
     * ⛔ `cp:dp:read` — the SAME permission the pane's own `ForbiddenState` names
     * (`FleetCapacityPane.tsx`), and the agreement is load-bearing rather than redundant.
     *
     * The pane degrades panel-by-panel: the cost column is `cp:billing:read`, separately holdable,
     * so an operator with DP read but no billing read gets the screen with one column absent. That
     * only works if the ENTRY gate is the weaker of the two. Gating the rail entry on
     * `cp:billing:read` would hide the whole screen from someone entitled to most of it, and the
     * per-panel degradation the pane carefully implements would never be reachable.
     */
    requiredPermission: 'cp:dp:read',
    // ⛔ NO `grantResourceType` — A22: an app grant must never light up Platform.
  },
  {
    /*
     * ⚠️ **THIS ENTRY CARRIED THE MANIFEST'S ONLY `railGroup` (`Operations`, with Alerts below it),
     * AND R9 RETIRED IT** (Q1, approved — see {@link ShellEntry}). The deck grouped In flight +
     * Alerts + two stubs on the rationale that all of them are platform-wide and none belongs to any
     * one tenant; that reasoning is intact, it is just no longer expressible as a *second* label
     * weight now that the rail draws every section as a labelled group.
     *
     * Alerts moved to Investigate (D5), so the old In flight and Alerts adjacency rule is gone.
     */
    id: 'platform-in-flight',
    section: 'platform',
    displayName: 'In flight',
    routePrefix: '/platform/in-flight',
    iconKey: 'Activity',
    loader: () => import('../panes/InFlightPane'),
    /*
     * `cp:dp:read`, and here the manifest is the door for every read but one. `InFlightPane`
     * renders a `ForbiddenState` on its dead-letter card only: the depth probes need
     * `cp:dlq:read`, and the pane sends none without it. The two remedy writes are absent without
     * `cp:provisioning-job:manage`. The job, health and feed reads have no second gate behind this
     * one.
     *
     * That is safe only because of what the permission means: `cp:dp:read` was reclassified
     * platform-admin-only by the corridor-security PR#190 IDOR fix, and per-DP ACLs were
     * explicitly rejected (the docblock of `EventStreamController`, which serves the fleet route).
     * So an all-fleet view behind it leaks nothing a holder cannot already read. ⛔ Do not soften
     * this to a broader permission such as `cp:platform:read` — that would turn a fleet-wide
     * operations view into something a non-platform-admin can open.
     */
    requiredPermission: 'cp:dp:read',
    // ⛔ NO `grantResourceType` — A22: an app grant must never light up Platform.
  },
  {
    /*
     * After In flight, and never above Tenants: declaration order decides the Platform landing.
     *
     * ⛔ `cp:platform-channel:read` — read off BOTH producers the screen uses:
     * `platform-channels.controller.ts:37-38` (list) and `:110-118` (subscribers + usage, the detail's two
     * panels). ⚠️ NOT `cp:platform:read`: that opens `GET /v1/channels` (the RELEASE rings, a different
     * registry) and would render a door onto three 403s.
     */
    id: 'platform-channels',
    section: 'platform',
    displayName: 'Platform channels',
    routePrefix: '/platform/channels',
    iconKey: 'Workflow',
    loader: () => import('../panes/PlatformChannelsPane'),
    requiredPermission: 'cp:platform-channel:read',
    // Parked: every data plane follows the beta channel for now, so the rail does not show this
    // screen. The route stays live. Delete the flag when channels per data plane come back.
    navHidden: true,
    // ⛔ NO `grantResourceType` — A22: an app grant must never light up Platform.
    detail: {
      // ⛔ MUST NOT equal the parent id — `AppLoader` caches `lazy(loader)` at module scope keyed by
      // `entry.id`, so a shared id serves the LIST pane on the detail route: a wrong screen, no error.
      id: 'platform-channel-detail',
      paramName: 'channelId',
      loader: () => import('../panes/PlatformChannelPane'),
    },
  },
  {
    /*
     * The Cost page (plan 2026-10-12-console-ward-billing, D1, D12). One entry replaces the four
     * `platform-billing-*` entries: the four pages answer four questions about one subject, so they
     * are four tabs of one page (`BillingCostPane`, URL key `tab`). The four old paths redirect to
     * their tab through the literal alias routes in `AppHost` and keep their query, so a bookmark
     * and the Fleet capacity link still land.
     *
     * Position: the last `platform` entry. `buildLandingCandidates` takes the first visible entry of
     * a section, so Platform keeps landing on Tenants. The S-40 test row pins the order.
     *
     * The gate is the imported read constant, never the literal and never `cp:billing:write`: the
     * write is separately holdable, and the Rate card tab hides its own controls without it. No
     * `grantResourceType` (A22): an app grant never lights up Platform.
     */
    id: 'platform-billing',
    section: 'platform',
    displayName: 'Cost',
    routePrefix: '/platform/billing',
    iconKey: 'Receipt',
    loader: () => import('../panes/BillingCostPane'),
    requiredPermission: BILLING_READ_PERMISSION,
  },
  {
    /*
     * ⭐ THE FIRST AND ONLY `releases` ENTRY. `SECTION_ORDER` has declared `releases` since WS-0
     * while `GLOBAL_ENTRIES` held nothing in it — a section that existed in the IA and rendered
     * nowhere. This is what fills it.
     *
     * The deck's Releases rail is three items, and as of the final cutover ALL THREE are routed:
     *   · Versions          — here.
     *   · Adoption          — `releases-adoption` below; `AdoptionPane.tsx` landed.
     *   · Release channels  — `releases-channels` below. ⚠️ This entry USED to record that it got no
     *                         entry at all ("`stub: true` in the deck; R7-DEC-10 forbids an inert
     *                         placeholder"). That was true only while the screen answered nothing new;
     *                         WS-7 §12.1 / R-27 gave it a real question ("who is on each channel right
     *                         now?") and it is now a live destination. ⛔ Left as a correction rather
     *                         than deleted: the *reason* still governs — a channels screen that merely
     *                         re-listed the Ring board's four rows would still be forbidden.
     *
     * ⛔ VOCABULARY (owner direction, 2026-08-05): the operator reads "release channel", never
     * "ring". That governs the DISPLAYED string only. `Ring`, `RINGS`, `RING_ORDINAL` and the
     * literal `:ring` path segment on the unpin route stay exactly as they are — renaming those is
     * an API break, not a copy edit. Rename what is read; never what is sent.
     */
    id: 'releases-versions',
    section: 'releases',
    displayName: 'Platform versions',
    routePrefix: '/releases/versions',
    iconKey: 'Package',
    loader: () => import('../panes/VersionsPane'),
    /*
     * ⛔ `cp:platform-channel:read`, which is the guard on the screen's PRIMARY data
     * (`version.controller.ts:53` — `GET /v1/releases/versions`), not the broader
     * `cp:platform:read` that `GET /v1/channels` uses (`channels.controller.ts:22`).
     *
     * The pane reads from both, so either would "work" as a gate — and that is exactly the trap.
     * Gating on `cp:platform:read` would open the screen for someone who cannot read a single
     * version row, i.e. the entire subject of the page, leaving them an empty table that looks
     * like a broken screen rather than a permission they lack. Gate on the permission that guards
     * what the screen is FOR.
     */
    requiredPermission: 'cp:platform-channel:read',
    // ⛔ NO `grantResourceType` — A22: an app grant must never light up Releases.
  },
  {
    /*
     * Channels (plan 2026-10-13-console-ward-releases, D2): the Ring board and Release channels
     * pages read the same pins under one permission, so they are two tabs of this page (URL key
     * `tab`, values `apps` and `tenants`). `/releases/ring-board` redirects to the Apps tab.
     */
    /*
     * ⛔ APPEND, after `releases-versions` (WS-7 §12.1) — Releases must keep LANDING on Versions
     * (`buildLandingCandidates` takes the first visible entry in a section).
     *
     * ⭐ This screen is deliberately NOT "list the four channels". The deck shipped no mockup and its
     * own comment says rings and release channels are the same four rows, so a literal reading would
     * duplicate the Ring board's columns — a door onto nothing new. R-27 approved instead: *"who is
     * on each channel right now?"*, the one question no shipped screen answers.
     *
     * ⛔ `cp:platform:read` — `release-engine/src/modules/channels/channels.controller.ts:22`, the
     * gate on `GET /v1/channels`, which is this screen's spine. ⚠️ NOT `cp:platform-channel:read`:
     * that opens the *platform* channel registry, a different noun (see `platform-channels` above).
     *
     * ⛔ VOCABULARY: the operator reads "release channel", never "ring" — displayed strings only.
     */
    id: 'releases-channels',
    section: 'releases',
    displayName: 'Channels',
    routePrefix: '/releases/channels',
    iconKey: 'Radio',
    loader: () => import('../panes/ReleaseChannelsPane'),
    requiredPermission: 'cp:platform:read',
    // ⛔ NO `grantResourceType` — A22's non-apps arm requires the omission.
  },
  {
    /*
     * Private channels (private-release-channels WS-5). Appended after `releases-channels`, so
     * Releases still lands on Versions: the landing takes the first visible entry in a section.
     *
     * The gate is `cp:channel:private:read`, the gate on `GET /v1/channels/private` (R-015).
     * `app_operator` holds it, so a developer sees this entry without `cp:platform:read`.
     *
     * `Lock` is the glyph, and here a lock is true: a tenant that has not accepted a share never
     * receives these builds (L-4, L-6). No other `releases` entry draws it, so the rail never shows
     * two rows on one glyph in this section.
     */
    id: 'releases-private-channels',
    section: 'releases',
    displayName: 'Private channels',
    routePrefix: '/releases/private-channels',
    iconKey: 'Lock',
    loader: () => import('../panes/PrivateChannelsPane'),
    requiredPermission: PRIVATE_CHANNEL_READ_PERMISSION,
    // No `grantResourceType`: A22's non-apps arm requires the omission.
    detail: {
      // Must not equal the parent id: `AppLoader` caches `lazy(loader)` keyed by `entry.id`.
      id: 'releases-private-channel-detail',
      paramName: 'channelId',
      loader: () => import('../panes/PrivateChannelPane'),
    },
  },
  {
    /*
     * FIRST among `access`: Access lands on Members, and declaration order is the landing mechanism
     * (`buildLandingCandidates` takes the first visible rail entry in a section). Members is the rail
     * parent of People, Groups and Roles, which sit indented under it (`navParent`). Its route sends
     * the viewer to the first of the three lists the viewer can read, People by default.
     *
     * The gate is `anyOf` the three list reads (I4), so a reader of any one list gets the parent. The
     * pending access-request count hangs off this id in `AppHost` (I3).
     */
    id: 'access-members',
    section: 'access',
    displayName: 'Members',
    routePrefix: '/access/members',
    iconKey: 'Users',
    loader: () => import('../panes/MembersPane'),
    requiredPermission: { anyOf: [OPERATOR_READ_PERMISSION, GROUP_READ_PERMISSION, ROLE_READ_PERMISSION] },
  },
  {
    /*
     * Indented under Members on the rail (`navParent`). Keep it above `access-reach`:
     * `accessOrderViolation` reads the order.
     */
    id: 'access-people',
    section: 'access',
    displayName: 'People',
    routePrefix: '/access/people',
    iconKey: 'Users',
    loader: () => import('../panes/PeoplePane'),
    navParent: 'access-members',
    requiredPermission: 'cp:operator:read',
    // ⛔ A `detail`, not a top-level entry — a top-level id would show in the Access rail with no
    // list behind it and would red the id-set `toEqual` in manifest-icons. Gated by its parent's
    // `cp:operator:read` (the READ permission); write permissions gate the controls INSIDE the
    // pane. Modelled on `platform-tenant-detail` (:154-158).
    // ⚠️ `paramName` MUST be `userId` — PersonPane throws by design when the param is absent, so a
    // rename here surfaces as that throw rather than as a routing error.
    detail: {
      id: 'access-person-detail',
      paramName: 'userId',
      loader: () => import('../panes/PersonPane'),
    },
  },
  {
    /*
     * Indented under Members on the rail (`navParent`).
     * ⛔ `cp:group:read` — `permissions-proxy.controller.ts:201-202` (list) and `:236-237` (members). Both
     * the list and the detail read only that permission; every write verb is separate
     * (`cp:group:create` :214, `:update` :221, `:delete` :228, `:manage-members` :243/:250) and gates a
     * control INSIDE the pane, never the door.
     */
    id: 'access-groups',
    section: 'access',
    displayName: 'Groups',
    routePrefix: '/access/groups',
    iconKey: 'Users2',
    loader: () => import('../panes/GroupsPane'),
    navParent: 'access-members',
    requiredPermission: 'cp:group:read',
    // ⛔ NO `grantResourceType` — A22.
    detail: {
      id: 'access-group-detail',
      // ⚠️ `groupId`, and `GroupPane` throws by design when the param is absent — so a rename here
      // surfaces as that throw rather than as a routing error. Same contract as `PersonPane`'s `userId`.
      paramName: 'groupId',
      loader: () => import('../panes/GroupPane'),
    },
  },
  {
    // Indented under Members on the rail (`navParent`).
    id: 'access-roles',
    section: 'access',
    displayName: 'Roles',
    routePrefix: '/access/roles',
    iconKey: 'ShieldCheck',
    loader: () => import('../panes/RolesPane'),
    navParent: 'access-members',
    // The catalogue is a pure READ. Assignment lives on People; this screen states so on itself.
    requiredPermission: 'cp:role:read',
    // ⛔ NO `grantResourceType` — A22: `GrantResourceType` is only 'app', and an app grant must
    // never light up the Access section.
    /*
     * ⛔ R9 §4.4 — Role detail. A `detail` SLOT on this entry, not a sibling entry: a sibling would
     * show "Role detail" in the Access rail with no list behind it. Route `/access/roles/:slug`, gate
     * INHERITED (`cp:role:read`) — §4.4 states all four of those, which is why this object is here
     * even though §2.3's paste-block does not carry one for it.
     *
     * ⚠️ `paramName: 'slug'`, not an id: roles are addressed by slug end to end
     * (`permissions-proxy.controller.ts`'s `roles/:slug`, and `identity.api.ts`'s `rolePath`).
     * ⭐ **R-35 (2026-08-09) — THE READ-ONLY RULING IS SUPERSEDED. Roles CRUD now ships here.**
     * This block used to read: *"⛔ READ-ONLY by ruling E2 / ACK-access-9 — the screen renders no
     * edit, delete or revoke-all control even though the producer has all three."* That became
     * ACTIVELY FALSE the moment 06a G-10 landed `role-edit`, `role-delete` and
     * `role-delete-revoke-all` on `RolePane`, and the pane-level stale-ruling sweep (T-10.10) covers
     * only `RolesPane`/`RolePane` — NOT this file — so nothing would have gone red on it.
     *
     * ⛔ Corrected rather than deleted, because the SHAPE of the old ruling still governs: the writes
     * are rendered **per the producer's own gates** — the entry stays gated on `cp:role:read` and each
     * write control hides ITSELF on the write grant. Gating this entry on a write would hide the
     * catalogue from a legitimate read-only population, which is the failure the original ruling was
     * protecting against and is still the wrong answer.
     *
     * ⚠️ A resource-scoped role's delete deliberately does NOT offer "revoke from all" (F-1): the
     * producer's revoke-all never touches `resource_grants`, so offering it would name a remedy that
     * cannot work. The console mirrors the producer; it does not improve on it.
     */
    detail: {
      id: 'access-role-detail',
      paramName: 'slug',
      loader: () => import('../panes/RolePane'),
    },
  },
  {
    /*
     * ⛔⛔ `cp:permission-grant:read`, NOT the old console's `cp:permission:grant`. The old value is a WRITE
     * permission gating a READ screen — see §1. Producer: `permissions-proxy.controller.ts:170-171`.
     * ⭐ `identity.api.ts` already exports this exact constant as `GRANT_READ_PERMISSION`; it is imported
     * at the top of this file rather than re-typed, so the manifest and the api module cannot drift.
     */
    // The rail says "App access" (access-members design, A1); the id and the route keep `grants`.
    id: 'access-grants',
    section: 'access',
    displayName: 'App access',
    routePrefix: '/access/grants',
    iconKey: 'KeyRound',
    loader: () => import('../panes/GrantsPane'),
    requiredPermission: GRANT_READ_PERMISSION,
    // ⛔ NO `grantResourceType` — A22. ⚠️ Sharpest here of anywhere: this screen's SUBJECT is app grants, so
    // "grants confer visibility" reads plausible. It would let an app owner read every grant on every
    // resource in the control plane.
  },
  {
    // After App access (D5). The rail says "Check access" (access-members design, A1); the id and the
    // route keep `reach`.
    id: 'access-reach',
    section: 'access',
    displayName: 'Check access',
    routePrefix: '/access/reach',
    iconKey: 'KeyRound',
    loader: () => import('../panes/ReachPane'),
    // Three panels, three gates. ⛔ A single gate would hide the whole screen from a principal
    // entitled to two of the three.
    requiredPermission: {
      anyOf: ['cp:operator:read', 'cp:permission-grant:read', 'cp:role:read'],
    },
  },
  {
    /*
     * After Check access (WS-17 of plan 2026-10-27-dp-telemetry-residency, R-042 item 3): the
     * approver queue of data plane telemetry access, on its own page and off People. The gate is
     * `cp:permission:grant`, the permission of every approver route of cp-auth-api
     * (`DP_ACCESS_REQUEST_ROUTES`). cp-auth-api also needs the live `platform_admin` row (R-064
     * item 1); the pane shows its 403 as a denial.
     */
    id: 'access-dp-access',
    section: 'access',
    displayName: 'DP access',
    routePrefix: '/access/dp-access',
    iconKey: 'Lock',
    loader: () => import('../panes/DpAccessPane'),
    requiredPermission: PERMISSION_GRANT_PERMISSION,
    // No `grantResourceType` (A22): a resource grant must never light up Access.
  },
  {
    /*
     * Last in Access (D5, D6). Off the rail (access-members design, A3 and I12): `CutoverBanner` on
     * the Roles list and on the Role page links here while a role holds a wildcard (`*` or
     * `prefix:*`). The route and its gate stay, because the banner link is the only door. Delete `navHidden` to bring
     * the rail item back.
     */
    id: 'access-cutover',
    section: 'access',
    displayName: 'Cutover dry-run',
    routePrefix: '/access/cutover',
    iconKey: 'ShieldCheck',
    loader: () => import('../panes/CutoverPane'),
    navHidden: true,
    requiredPermission: 'cp:operator:read',
  },
  {
    /*
     * The Investigate block starts here (WS-17 of plan 2026-10-27-dp-telemetry-residency, R-042
     * item 2): Fleet health is the first item, so Investigate lands on the problems of every data
     * plane. The gate is `cp:platform:read`, the read guard of the alerting routes the page reads;
     * its data plane names and its Grafana links gate themselves inside the pane.
     */
    id: 'platform-fleet-health',
    section: 'investigate',
    displayName: 'Fleet health',
    routePrefix: '/platform/fleet-health',
    iconKey: 'HeartPulse',
    loader: () => import('../panes/FleetHealthPane'),
    requiredPermission: 'cp:platform:read',
    // No `grantResourceType` (A22): an app grant must never light up Investigate.
  },
  {
    /*
     * Second in Investigate, after Fleet health, then the other four entries in D5 order. Each route
     * keeps its old prefix (plan I1).
     */
    id: 'platform-alerts',
    section: 'investigate',
    displayName: 'Alerts',
    routePrefix: '/platform/alerts',
    iconKey: 'Bell',
    loader: () => import('../panes/AlertsPane'),
    /*
     * ⛔ `cp:platform:read` — the READ guard on the alerting surface
     * (`alerting.controller.ts:38,57,71`), NOT the `cp:settings:write` its editors need.
     *
     * Gating the entry on the write permission would hide the whole screen from an operator who
     * can legitimately READ every rule and destination, which is the common case: the dev
     * `platform-operator` persona holds `cp:platform:read` and deliberately does NOT hold
     * `cp:settings:write`, so it renders the screen with the editors permission-hidden — the live
     * R7-DEC-5 demonstration. Gate on read; let the writes hide themselves.
     */
    requiredPermission: 'cp:platform:read',
    // No `grantResourceType` (A22): an app grant must never light up Investigate.
  },
  {
    /*
     * Second in Investigate, after Alerts (#1251, WS-7 §10.1).
     *
     * ⛔ `ScrollText` is NOT a new glyph — it is already registered for `app-logs`, and sharing one
     * across sections has precedent (`Activity` serves `platform-in-flight` and
     * `app-history`). ⚠️ An UNREGISTERED key renders `LayoutDashboard` with no error, no
     * warning and no test failure except `manifest-icons.test.ts`.
     */
    id: 'platform-cp-logs',
    section: 'investigate',
    displayName: 'Control-plane logs',
    routePrefix: '/platform/cp-logs',
    iconKey: 'ScrollText',
    loader: () => import('../panes/CpLogsPane'),
    /*
     * ⛔ ONE STRING GATES THE ENTRY, THE POD LIST AND THE LOG (R-27) — so this screen has no
     * per-panel degradation, and the manifest is the ONLY door, exactly as `platform-in-flight`
     * records above. It is safe for the same reason: `cp:control-plane:pod-log:read` is
     * platform-admin-only (`permission-classification.ts`, migration `0027`), so an all-CP view
     * behind it leaks nothing a holder cannot already read.
     *
     * ⛔ Do not soften it to a broader permission, and ⛔ do not split it in two "for symmetry": the
     * pane's missing panel-level `ForbiddenState` is that ruling's consequence, and a second string
     * would make it an unreachable branch that costs the file its 100%.
     */
    requiredPermission: CP_POD_LOG_READ_PERMISSION,
    // No `grantResourceType` (A22): an app grant must never light up Investigate.
  },
  {
    /*
     * Third in Investigate, after Control-plane logs (WS-7 §11.1).
     *
     * ⛔ A FLAT STRING, not an `anyOf`. Every route this screen touches is `cp:dlq:read` or
     * `cp:dlq:manage` (the site-admin-api constants `DLQ_READ_PERM` and `DLQ_MANAGE_PERM`), and the
     * manage half hides its own controls (R7-DEC-5's hide axis) and leaves the door open.
     *
     * ⚠️ The screen ALSO reads the data-plane list, which is `cp:dp:read` — a separately holdable
     * grant. That is gated INSIDE the pane (`QueuesPane`'s `useHasPermission(DP_READ_PERMISSION)`
     * shell), not by widening this entry: widening it would hide the whole screen from a principal
     * entitled to half of it, which is the failure `platform-in-flight`'s `anyOf` exists to avoid in
     * the other direction.
     */
    id: 'platform-queues',
    section: 'investigate',
    displayName: 'Dead letters',
    routePrefix: '/platform/queues',
    iconKey: 'Layers',
    loader: () => import('../panes/QueuesPane'),
    requiredPermission: 'cp:dlq:read',
    // No `grantResourceType` (A22): an app grant must never light up Investigate.
  },
  {
    /*
     * Fourth in Investigate (D5): Grafana is a watch page, an iframe of `/grafana`, and no setting.
     * ⛔ The gate is `cp:observability:read-traces`, the key `AuthController.authCheck` in cp-auth-api
     * enforces before ext-authz admits a caller to Grafana, so the nav entry and the server agree. The
     * spec `grafana-access-gate-parity.spec.ts` in `@zion/cp-parity-tests` reads both keys and holds them equal.
     *
     * ⛔ The pane is an iframe and makes no HTTP call, so it is OUT of `permission-gate-parity.test.ts`'s
     * corpus BY CONSTRUCTION, not by allowlist: that gate's obligation is "a component importing a
     * permissioned api module owes a gate", and this component imports none. Adding an exemption row is
     * the plausible-looking wrong edit.
     */
    id: 'settings-grafana',
    section: 'investigate',
    displayName: 'Grafana',
    routePrefix: '/settings/grafana',
    iconKey: 'LineChart',
    loader: () => import('../panes/GrafanaPane'),
    requiredPermission: 'cp:observability:read-traces',
    // No `grantResourceType` (A22): an app grant must never light up Investigate.
  },
  {
    /*
     * Last in Investigate (D5). The gate is `cp:audit:read`, the guard of `AuditLogProxyController` in
     * site-admin-api. It was one of R7-DEC-10's doorless stubs; the producer is live now.
     */
    id: 'access-audit',
    section: 'investigate',
    displayName: 'Audit',
    routePrefix: '/access/audit',
    iconKey: 'FileText',
    loader: () => import('../panes/AuditPane'),
    requiredPermission: 'cp:audit:read',
    // No `grantResourceType` (A22): an app grant must never light up Investigate.
  },
  {
    id: 'app-catalog',
    section: 'apps',
    displayName: 'App catalog',
    routePrefix: '/apps',
    iconKey: 'LayoutGrid',
    loader: () => import('../panes/AppCatalogPane'),
    /*
     * `anyOf`, so a principal with `cp:app:create` alone reaches the catalogue. It sees an empty
     * list (the server filters it by grant) and the New app door.
     * Since 2026-10-26-appdev-permissions A-008, the site `app_developer` holds both keys.
     * So it sees every app.
     * The grant leg still matches `cp:app:read` for a principal whose grants are per app.
     * `app_owner` and `app_admin` grants also carry `cp:app:create`, so the grant leg matches it
     * too; such a grant also carries `cp:app:read`.
     */
    requiredPermission: { anyOf: ['cp:app:read', 'cp:app:create'] },
    grantResourceType: 'app',
    /*
     * ⛔ #1242 — "New app was never ported". A `create` slot, NOT a sibling entry: `AppHost` builds
     * `railItems` from every visible GlobalEntry with a `routePrefix`, so a sibling would ship "New app" as
     * a permanent Apps nav item pointing at a blank form (the `railItems` filter in `AppHost`).
     *
     * ⚠️ Two independent mechanisms make `/apps/new` safe beside `/apps/:appId`, and `entry-create.test.tsx`
     * asserts BOTH: react-router ranks the static `new` above the sibling dynamic segment, and
     * `resolveActiveSection` is longest-prefix over `routePrefix` so Apps stays lit.
     */
    create: {
      id: 'app-create',
      segment: 'new',
      loader: () => import('../panes/NewAppPane'),
    },
  },
  {
    /*
     * The kernel-right queue of every app, in Apps after the catalog (D5, D7). Site-only: the one
     * exception to A22's apps arm (R1), because both queue routes check the site permission only.
     */
    id: 'access-kernel-rights',
    section: 'apps',
    displayName: 'Kernel rights',
    routePrefix: '/access/kernel-rights',
    iconKey: 'Fingerprint',
    loader: () => import('../panes/KernelRightsQueuePane'),
    requiredPermission: KERNEL_RIGHTS_DECIDE_PERMISSION,
    // No `grantResourceType` (R1).
  },
  {
    /*
     * The library right queue of every app and library (2026-10-12-shared-libraries, WS-13), in Apps
     * after Kernel rights (plan I8). Site-only like Kernel rights, so it is the second A22 exception.
     */
    id: 'access-library-rights',
    section: 'apps',
    displayName: 'Library rights',
    routePrefix: '/access/library-rights',
    iconKey: 'Library',
    loader: () => import('../panes/LibraryRightsQueuePane'),
    requiredPermission: LIBRARY_RIGHTS_DECIDE_PERMISSION,
    // No `grantResourceType` (R1, I8).
  },
  {
    /*
     * ⭐ THE FIRST `settings` ENTRY. `SECTION_ORDER` has declared `settings` since the rail landed while
     * `GLOBAL_ENTRIES` held nothing in it — a section in the IA that rendered nowhere. This fills it, and
     * ⛔ being FIRST it decides where Settings LANDS (`buildLandingCandidates`). Ruled deliberately: the
     * EnvironmentConfig snapshot is the one an operator opens during an incident.
     *
     * ⛔⛔ `cp:env-config:read`, NOT `cp:settings:read`. Producer:
     * `settings/environment-config.controller.ts:18-19`. The old console put this page and Observability
     * retention under ONE `cp:settings:read` entry, so a `cp:settings:read` holder without
     * `cp:env-config:read` got a door onto a 403. N-1 splitting them is what lets each carry its own gate —
     * ⇒ do NOT "simplify" the two Settings entries into one screen with two panels.
     */
    id: 'settings-platform',
    section: 'settings',
    displayName: 'Platform settings',
    routePrefix: '/settings/platform',
    iconKey: 'Settings',
    loader: () => import('../panes/PlatformSettingsPane'),
    requiredPermission: 'cp:env-config:read',
    // ⛔ NO `grantResourceType` — A22.
  },
  {
    /*
     * ⛔ SECOND. `cp:settings:READ` — `observability-retention.controller.ts:37-38`. ⚠️ NOT
     * `cp:settings:write` (`:44-45`), which the form's Save needs: gating the entry on write would hide the
     * whole screen from an operator who can legitimately read every retention window. Gate on read; let the
     * write hide itself — the same rule `platform-alerts` already states.
     */
    id: 'settings-observability',
    section: 'settings',
    displayName: 'Observability retention',
    routePrefix: '/settings/observability',
    iconKey: 'Timer',
    loader: () => import('../panes/ObservabilityRetentionPane'),
    requiredPermission: 'cp:settings:read',
    // ⛔ NO `grantResourceType` — A22.
  },
  {
    /*
     * P-1 (WS-6, 06a). The four entries below come after `settings-observability`, at the end
     * of `GLOBAL_ENTRIES`: `buildLandingCandidates` takes the FIRST
     * entry of a section in declaration order, so inserting above `settings-platform` would silently
     * move where an operator lands in Settings.
     *
     * ⛔ `cp:idp:read` — `site-admin-api/src/modules/permissions/auth-providers-proxy.controller.ts:41`
     * and the producer at `cp-auth-api/src/modules/identity/auth-providers.controller.ts:46`. ⚠️ NOT
     * `cp:settings:read`: the `cp:idp:*` family exists and a `cp:settings:read` holder without it would
     * get a door onto a 403. Every write (`:create`/`:update`/`:delete`) gates a control INSIDE the pane.
     */
    id: 'settings-idp-providers',
    section: 'settings',
    displayName: 'Identity providers',
    routePrefix: '/settings/idp-providers',
    iconKey: 'Fingerprint',
    loader: () => import('../panes/IdpProvidersPane'),
    requiredPermission: 'cp:idp:read',
    // ⛔ NO `grantResourceType` — A22: `GrantResourceType` is only 'app', and an app grant must never
    // light up Settings.
  },
  {
    /*
     * Feature 2026-10-22-cp-identity-token, D11 and R-014: the read-only settings of the CP identity
     * token issuer. Declared directly after `settings-idp-providers`, because adjacency is the only
     * grouping channel left, and after `settings-platform`, so Settings still lands on Platform
     * settings. The gate is `IDP_READ_PERMISSION`, the permission of the producer route
     * `GET /v1/admin/auth-providers/identity-token` and of its site-admin-api forward. The page
     * gates its Recent mints section on `cp:audit:read` itself. `Lock` is free in `settings`.
     */
    id: 'settings-identity-token',
    section: 'settings',
    displayName: 'Identity token',
    routePrefix: '/settings/identity-token',
    iconKey: 'Lock',
    loader: () => import('../panes/IdentityTokenPane'),
    requiredPermission: IDP_READ_PERMISSION,
    // This entry carries no `grantResourceType` (A22).
  },
  {
    /*
     * ⭐ P-1 (WS-6 · 06a). ⛔ `cp:scim-client:read` — `permissions-proxy.controller.ts:262-263`.
     * ⚠️ The reachable path is `/v1/admin/permissions/scim-clients`; `/v1/admin/scim-clients` is the
     * cp-auth-api spelling and answers a JSON 404 from the browser.
     * ⛔ ADJACENT to the other identity-integration entries — `railGroup` is retired, so adjacency
     * is the only grouping channel left.
     */
    id: 'settings-scim-clients',
    section: 'settings',
    displayName: 'SCIM clients',
    routePrefix: '/settings/scim-clients',
    iconKey: 'UserCog',
    loader: () => import('../panes/ScimClientsPane'),
    requiredPermission: 'cp:scim-client:read',
    // ⛔ NO `grantResourceType` — A22.
  },
  {
    /*
     * ⭐ WS-6 · 06e. ⛔ Gated on the READ; every write hides itself. This follows the rule
     * `settings-observability` already states above: gating the entry on write would hide the whole
     * screen from an operator who can legitimately read everything on it.
     */
    id: 'settings-oauth-clients',
    section: 'settings',
    displayName: 'OAuth clients',
    routePrefix: '/settings/oauth-clients',
    iconKey: 'KeySquare',
    loader: () => import('../panes/OAuthClientsPane'),
    requiredPermission: SETTINGS_READ_PERMISSION,
    // ⛔ NO `grantResourceType` — A22 (`manifest-invariants.test.ts:134-175`).
  },
  // The registration hosts are the Self-registration tab of `settings-oauth-clients` since plan
  // 2026-10-21-console-ward-oauth-clients; AppHost redirects `/settings/dcr-hosts` there.
  {
    /*
     * Plan 2026-10-21-release-on-zion-build, WS-17 (R-042 item 1, R-044 item 2): the machine
     * identities, one per job of `release.yml`, with their trust policies and grants. Appended last,
     * so Settings still lands on Platform settings, and beside the other identity integrations (SCIM
     * clients, OAuth clients): a machine identity configures where a token comes from, as they do.
     *
     * The gate is the imported read constant, the permission of `GET /v1/admin/machine-principals`.
     * Every write gates a control inside the pages. `Bot` is free in `settings`. No
     * `grantResourceType` (A22).
     */
    id: 'settings-machine-identities',
    section: 'settings',
    displayName: 'Machine identities',
    routePrefix: MACHINE_IDENTITIES_ROUTE,
    iconKey: 'Bot',
    loader: () => import('../panes/MachineIdentitiesPane'),
    requiredPermission: MACHINE_PRINCIPAL_READ_PERMISSION,
    detail: {
      // Must not equal the parent id: `AppLoader` caches `lazy(loader)` keyed by `entry.id`.
      id: 'settings-machine-identity-detail',
      paramName: MACHINE_IDENTITY_PARAM,
      loader: () => import('../panes/MachineIdentityPane'),
    },
  },
];

export const APP_SCOPED_ENTRIES: readonly AppScopedEntry[] = [
  {
    id: 'app-overview',
    section: 'apps',
    displayName: 'Overview',
    subPath: '',
    iconKey: 'LayoutDashboard',
    loader: () => import('../panes/AppOverviewPane'),
    requiredPermission: 'cp:app:read',
    grantResourceType: 'app',
  },
  {
    /*
     * WS-F, relayed by the lead (ACK-shell-MANIFEST-1). ⭐ THE EXTENSION POINT IN USE: adding an R7
     * screen is adding ONE object here — no route edit, no rail edit, no header edit.
     *
     * ⛔ `requiredPermission` is the fixtures read, NOT `cp:app:read`. Read off the producer: the
     * deployments and diagnostics routes check `cp:app:fixtures:read` (R-006 and R-011 of
     * 2026-10-20-cp-permissioning), and the resource leg of `useHasPermission` is EXACT-MATCH with
     * no wildcard expansion, so gating the rail entry on `cp:app:read` would render a door that
     * 403s. Do NOT "simplify" it to match Overview; a real principal can hold one grant without
     * the other.
     */
    id: 'app-fleet',
    section: 'apps',
    displayName: 'Fleet',
    subPath: 'fleet',
    iconKey: 'ServerCog',
    loader: () => import('../panes/AppFleetPane'),
    // Either key for the roll window (R-011 item 3): a pod of the previous release checks the runtime key.
    requiredPermission: { anyOf: ['cp:app:fixtures:read', 'cp:app:runtime:read'] },
    grantResourceType: 'app',
  },
  {
    /*
     * ⛔ `anyOf`, and it is load-bearing. The screen reads `release-chain`
     * (`cp:app:adoption:read`) plus diagnostics/history (`cp:app:read`), and a single-permission
     * gate would hide the WHOLE screen from someone entitled to two of its three panels.
     * `entryAllows` handles the union on both legs — site through `hasPermission` (wildcards
     * expand), grant through `wanted.includes(g.permission)` (exact equality, no expansion).
     */
    id: 'app-rollout',
    section: 'apps',
    displayName: 'Rollout',
    subPath: 'rollout',
    iconKey: 'GitBranch',
    loader: () => import('../panes/AppRolloutPane'),
    requiredPermission: { anyOf: ['cp:app:adoption:read', 'cp:app:read'] },
    grantResourceType: 'app',
  },
  {
    /*
     * ⭐ WS-6 · 06b G7. ⛔ APPENDED immediately after `app-rollout`: the two are the same subject at
     * two grains (Rollout is the per-data-plane cut, Adoption the per-tenant one), and
     * `AppRolloutPane` carries a signpost link here for exactly that reason. Adjacency is the only
     * grouping channel left since `railGroup` was retired.
     *
     * ⛔ `cp:app:adoption:read` — the producer's gate (`app-admin.controller.ts:44`). NOT
     * `cp:app:read`: the two are separately holdable and this screen reads nothing else. All three
     * of its reads go through ONE client function (`getAdoption`, `appRollout.api.ts:222`).
     */
    id: 'app-adoption',
    section: 'apps',
    displayName: 'Adoption',
    subPath: 'adoption',
    iconKey: 'GitCompare',
    loader: () => import('../panes/AppAdoptionPane'),
    requiredPermission: ADOPTION_PERMISSION,
    grantResourceType: 'app',
  },
  {
    /*
     * ⭐⭐ **`displayName` IS `Fixtures`. OWNER RULING, 2026-08-07: *"Just call it 'Fixtures'."***
     *
     * It read `Fixtures, services & pods` — the longest label in the product at **162.4px** (measured
     * in headless Chrome at `--text-xs`), and therefore the single constraint that set the nav rail's
     * minimum width. `02-NAV-AND-PORT.md` §7.3 derived a 264px rail from it and escalated the trade as
     * **Q2**: *"the only other lever is a copy change to that label, which is the owner's to make."*
     * The owner took that lever.
     *
     * ⇒ ⛔ **DO NOT RESTORE THE LONGER LABEL.** Re-measured 2026-08-07 with it gone: the widest of all
     * 31 destinations is now `Observability retention` at **146.7px**, the rail's floor drops
     * 237.4px → **207.7px**, and the rail therefore stays at its shipped `w-60` (240px) instead of
     * taking 24px of content width from every screen. Restoring three words here re-opens a
     * whole-console layout decision.
     *
     * ⚠️ The label is the ONLY thing that changed. Per **N-9** the id, the `subPath`, the route and
     * `data-testid="nav-app-fixtures"` are byte-identical — `routing-branches.test.tsx` and
     * `doorless-panes.test.tsx`'s G5 guard name them, and their subjects are untouched by a copy edit.
     */
    id: 'app-fixtures',
    section: 'apps',
    displayName: 'Fixtures',
    subPath: 'fixtures',
    iconKey: 'Boxes',
    loader: () => import('../panes/AppFixturesPane'),
    /*
     * `cp:app:fixtures:read` (D9 of 2026-10-20-cp-permissioning): `app_owner`, `app_maintainer`
     * and `app_viewer` hold it per app, so an app viewer sees the tab and, without
     * `cp:app:runtime:read`, does not see Pod logs. The pane names `cp:app:fixtures:read` in each
     * refusal of its three reads, which the server gates on that key (R-006).
     */
    // Either key for the roll window (R-006): a pod of the previous release still gates the reads on the runtime key.
    requiredPermission: { anyOf: ['cp:app:fixtures:read', 'cp:app:runtime:read'] },
    grantResourceType: 'app',
  },
  {
    /*
     * ⭐ WS-6 · 06b G3. ⛔ APPENDED after `app-fixtures` — this screen IS the fixture configuration
     * surface (`AppConfigPane`'s region is labelled "Fixture configuration"), so it sits beside the
     * fixtures list rather than at the end of the rail.
     *
     * ⛔ `cp:app:read` is the gate, read off the producer: all three reads this screen makes are
     * `@RequireResourcePermission('cp:app:read', 'appId')` (`app-fixture-config.controller.ts:34,41,84`).
     * The WRITES need `cp:app:write` and are gated INSIDE the pane — a principal who can read the
     * config and not change it is a real population and must still see the screen.
     *
     * `iconKey: 'Settings'` is already registered. Sharing it with the `settings` SECTION is fine and
     * has precedent: the rail renders one section at a time, so the two can never appear together.
     */
    id: 'app-config',
    section: 'apps',
    displayName: 'Configuration',
    subPath: 'config',
    iconKey: 'Settings',
    loader: () => import('../panes/AppConfigPane'),
    requiredPermission: 'cp:app:read',
    grantResourceType: 'app',
  },
  {
    /*
     * WS-6, 06b G4. The gate is `APP_BUILD_PERMISSION`, `cp:app:read`, as the producer
     * `AppsController.getBuildStatus` asks since #1084 (plan 2026-10-13-console-ward-app-drill-in,
     * A-4). This screen reads the build record; `bin/zion-app` produces the builds.
     *
     * `navHidden`: the rail shows no Build item, and the route stays live (D2). The Overview shows the
     * build content as a card, so a bookmark to `/apps/:appId/build` still opens this screen.
     *
     * `Package` is already registered; it serves `releases-versions`, a different section, so the two
     * are never on screen at the same time.
     */
    id: 'app-build',
    section: 'apps',
    displayName: 'Build',
    subPath: 'build',
    iconKey: 'Package',
    loader: () => import('../panes/AppBuildPane'),
    requiredPermission: APP_BUILD_PERMISSION,
    grantResourceType: 'app',
    navHidden: true,
  },
  {
    /*
     * ⭐ **A DELIBERATE DIVERGENCE FROM THE DECK, AUTHORISED BY RULING — NOT AN UNREVIEWED ADDITION.**
     *
     * ⛔ READ THIS BEFORE DELETING THE ENTRY. The deck's Apps rail has NINE entries and **History is
     * not one of them** (catalog · home · fleet · rollout · fixtures · console · metrics · capacity ·
     * access). A parity audit that checks the rail against `mockups/` will therefore flag this as an
     * unauthorised extra screen — a parity violation in the opposite direction — and the plausible
     * "fix" is to remove it. That would delete a screen the platform has no other way to show.
     *
     * **The authority is lead ruling ACK-panes-5**, recorded in the pane's own header
     * (`AppHistoryPane.tsx:2-9`). It chose *"a sixth screen over a strip on Overview"* on a
     * producer-shape argument: `GET /v1/admin/apps/:appId/history` returns **transitions AND uptime in
     * one page**, so a strip on Overview would call a **keyset-paginated** transitions endpoint purely
     * to read two numbers. Re-confirmed 2026-08-05 (ACK-dev-apps-2).
     *
     * ⚠️ It does NOT belong in the unbuilt `06-metrics` instead — that screen is pinned PromQL charts,
     * a fixture/version/DP filter set and month-to-date cost. Different subject, checked before ruling.
     *
     * ⛔ **WHY IT WAS MISSING (parity gap G5).** The pane shipped COMPLETE — 459 lines, default export,
     * window selector, keyset pagination, uptime table, its own 6-describe suite — with no entry here.
     * Routes under `/apps/:appId/*` come from this array alone (`AppHost.tsx:320`), so **no URL reached
     * it**, and `AppScopeRoute`'s unknown-sub-path arm redirects to the app root, which is
     * byte-identical to a mistyped URL. Every existing gate was green over it because all of them run
     * *manifest → pane*; the unasserted direction was *pane → manifest*. That direction is now gated by
     * `shell/__tests__/doorless-panes.test.tsx`.
     *
     * ⛔ The fixtures read, NOT `cp:app:read`. Read off the producer: `AppHistoryController` checks
     * `cp:app:fixtures:read` since R-011 of 2026-10-20-cp-permissioning, the key Fleet and Fixtures
     * read on, so this screen adds no new permission. The grant leg of `entryAllows` is exact-match
     * with no wildcard expansion, so `cp:app:read` here would render a rail link that 403s.
     *
     * `iconKey: 'Activity'` is a registered `ICONS` key. The rail alone draws it: the page title is
     * text only. `platform-in-flight` also uses it, which is fine — the rail renders one section at a
     * time, so the two can never appear together.
     */
    id: 'app-history',
    section: 'apps',
    displayName: 'History & uptime',
    subPath: 'history',
    iconKey: 'Activity',
    loader: () => import('../panes/AppHistoryPane'),
    // Either key for the roll window (R-011 item 3): a pod of the previous release checks the runtime key.
    requiredPermission: { anyOf: ['cp:app:fixtures:read', 'cp:app:runtime:read'] },
    grantResourceType: 'app',
  },
  {
    /*
     * ⭐ **LAYER C (D9) — the app-team operator's compute surface.** The persona Dave added to this
     * console has had no compute surface at all; this is it.
     *
     * ⛔ `cp:app:read`, NOT `cp:app:compute:update` and NOT `cp:app:runtime:read`. Read off the
     * PRODUCER: `GET /v1/admin/apps/:appId/compute` is
     * `@RequireResourcePermission('cp:app:read', 'appId')` (40-ui §2.1).
     *   · Gating on the WRITE permission would hide the whole screen from an app viewer who may
     *     legitimately read every effective value — the rule `platform-alerts` and
     *     `settings-observability` already state: gate on read, let the write hide itself.
     *   · Gating on `cp:app:runtime:read` would hide it from a principal who holds `cp:app:read`
     *     alone, i.e. from the read this screen is FOR. The grant leg is exact-match with no
     *     wildcard expansion, so a near-miss here renders a rail link that 403s.
     *
     * ⚠️ `Cpu` must be registered in `NavRail`'s `ICONS` map in this same change — an unregistered
     * key renders `LayoutDashboard` with no error, no warning and no test failure.
     */
    id: 'app-compute',
    section: 'apps',
    displayName: 'Compute',
    subPath: 'compute',
    iconKey: 'Cpu',
    loader: () => import('../panes/AppComputePane'),
    requiredPermission: 'cp:app:read',
    /* ⛔ REQUIRED by A22's apps arm: an Apps entry that omits it is invisible to every
       resource-scoped principal — precisely the persona this screen exists for. */
    grantResourceType: 'app',
  },
  {
    /*
     * ⚠️ THIS ONE MUST PRESERVE ITS QUERY STRING. Fixtures' pod rows deep-link to
     * `/apps/:appId/logs?dp=…&pod=…` so the screen opens pre-scoped. If routing drops the search
     * params the link lands on an UNSCOPED screen — which looks like it worked, which is why it is
     * pinned by a test (`routing-branches.test.tsx`) rather than left to the router's defaults.
     */
    id: 'app-logs',
    section: 'apps',
    displayName: 'Pod logs',
    subPath: 'logs',
    iconKey: 'ScrollText',
    loader: () => import('../panes/AppLogsPane'),
    requiredPermission: 'cp:app:runtime:read',
    grantResourceType: 'app',
  },
  {
    id: 'app-access',
    section: 'apps',
    displayName: 'Access',
    subPath: 'access',
    iconKey: 'Users',
    loader: () => import('../panes/AppAccessPane'),
    requiredPermission: 'cp:app:read',
    grantResourceType: 'app',
  },
  {
    /*
     * Kernel rights of one app (kernel apps, WS-9a, `design-docs/WS-9.md` section 3.3). The entry
     * gates on the read, `cp:app:read`, so every app reader sees the rights and the history. The
     * request and the release-source edit hide themselves on `cp:app:kernel-rights:request`, and
     * release-engine decides each write. `ShieldCheck` is free in the apps rail group.
     */
    id: 'app-kernel-rights',
    section: 'apps',
    displayName: 'Kernel rights',
    subPath: 'kernel-rights',
    iconKey: 'ShieldCheck',
    loader: () => import('../panes/AppKernelRightsPane'),
    requiredPermission: KERNEL_RIGHTS_READ_PERMISSION,
    grantResourceType: 'app',
  },
  {
    /*
     * The shared libraries of one app or library (2026-10-12-shared-libraries, WS-13, R-013, R-018).
     * The entry gates on the read, `cp:app:read`, so every app reader sees the libraries and the
     * requests. The request hides itself on `cp:app:kernel-rights:request` and the consumer config
     * on `cp:app:write`; release-engine decides each write. `Library` is free in the apps group.
     */
    id: 'app-libraries',
    section: 'apps',
    displayName: 'Libraries',
    subPath: 'libraries',
    iconKey: 'Library',
    loader: () => import('../panes/AppLibrariesPane'),
    requiredPermission: LIBRARY_RIGHTS_READ_PERMISSION,
    grantResourceType: 'app',
  },
  {
    /**
     * ⭐⭐ **Availability (#2568) — the tab whose absence made a running app invisible.**
     *
     * ## The gate is `cp:app:read`, the same key as the read of the pane
     *
     * Since D5 of 2026-10-20-cp-permissioning the producer gates the list on `cp:app:read` on the
     * app (`@RequireResourcePermission` on the licences controller), and the offer and the
     * withdraw on `cp:app:licence:grant` on the app. So every app reader reaches the tab and reads
     * the list, and an app owner also offers the app. Before D5 the read was `cp:billing:read`, and
     * an app owner could not find out why nobody saw the published app: the state #2568 measured.
     *
     * ⛔ The DATA is gated by the server and by nothing here — a manifest entry has never been an
     * authorization boundary, which is exactly why `NewAppPane` carries its own `ForbiddenState`
     * too.
     *
     * ⚠️ `grantResourceType: 'app'` is required of every Apps entry by A22, and it is honest here
     * because the entry's OWN permission is `cp:app:read`, which IS resource-grantable.
     *
     * ⚠️ LAST in the rail, deliberately. It is the question nobody knew to ask, not the one an
     * operator opens during an incident, and promoting it above *Fleet* or *Rollout* would rank a
     * discovery aid over the triage path.
     */
    id: 'app-availability',
    section: 'apps',
    displayName: 'Availability',
    subPath: 'availability',
    /* ⛔ `Eye` — "who can see this". Registered in `NavRail`'s `ICONS`; an unregistered key falls
       back to `LayoutDashboard` in silence, which `manifest-icons.test.ts` is what catches. */
    iconKey: 'Eye',
    loader: () => import('../panes/AppAvailabilityPane'),
    requiredPermission: 'cp:app:read',
    grantResourceType: 'app',
  },
  // R9 adds Metrics & usage · Capacity.
];

// ⛔ R6 SHIPS EXACTLY THESE TWO ENTRIES AND NO PLACEHOLDERS. The mockups render the unbuilt
// screens with an `n/a` tag, and the mockups README's deviation 5 says explicitly: "That is a
// prototype affordance, not a product proposal — in the real shell those entries either exist
// or are filtered out by permission. Do not ship the `n/a` tag." Shipping seven dead rail
// controls would also contradict the same README's pushback #1 ("eleven screens is a good
// design artifact and a bad backlog").
//
// Considered and rejected: shipping them as inert `.notbuilt` panels — it advertises doors
// that do not open, and R7 adding a rail entry is a one-object change either way, so it buys
// nothing.
//
// R7 kept five screens doorless (R7-DEC-10). The final cutover routed `platform-queues`, now
// labelled `Dead letters` (D1 of the 2026-10-12 console-ward-queues plan); see its entry above.
